import type {
  Rule,
  RuleContext,
  RuleResult,
  PreprocessingResult,
  FilteredElement,
  FilterReason,
  PreprocessingStats
} from "../types/index.js";
import { logger, Logger } from "../utils/logger.js";
import { ImageProcessor } from "../services/image-processor.js";
import type { ImageProcessingConfig, ImageProcessingResult } from "../types/image-processing.js";

/**
 * Figma数据预处理器
 * 负责过滤无效元素、转换节点类型、识别遮挡关系
 */
export class FigmaDataPreprocessor {
  private filteredElements: FilteredElement[] = [];
  private processingStats: PreprocessingStats = {
    totalOriginal: 0,
    filtered: 0,
    retained: 0,
    filteringRate: 0
  };
  private imageProcessor?: ImageProcessor;
  private imageProcessingResult?: ImageProcessingResult;
  private fileKey?: string;
  private accessToken?: string;
  private imageConfig: ImageProcessingConfig;
  private logger: Logger;

  constructor(config: {
    fileKey?: string;
    accessToken?: string;
    imageConfig?: Partial<ImageProcessingConfig>;
  } = {}) {
    this.fileKey = config.fileKey;
    this.accessToken = config.accessToken;
    this.imageConfig = {
      enableImageDetection: true,
      enableImageDownload: true,
      imageFormats: ['png', 'jpg', 'svg'],
      maxImageSize: 5 * 1024 * 1024,
      scale: 2,
      format: 'png',
      ...config.imageConfig
    };
    this.logger = new Logger();

    // 如果有文件密钥和访问令牌，初始化图片处理器
    if (this.fileKey && this.accessToken) {
      this.imageProcessor = new ImageProcessor(
        this.fileKey,
        this.accessToken,
        this.imageConfig
      );
    }
  }

  /**
   * 预处理Figma节点数据
   */
  async preprocessNodes(nodes: any[]): Promise<PreprocessingResult> {
    this.processingStats.totalOriginal = this.countTotalNodes(nodes);
    
    const validNodes = this.filterValidNodes(nodes);
    const convertedNodes = this.convertNodeTypes(validNodes);
    
    // 处理图片
    let finalNodes = convertedNodes;
    if (this.imageProcessor && this.imageConfig.enableImageDetection) {
      try {
        this.logger.info('开始图片处理');
        this.imageProcessingResult = await this.imageProcessor.processImages(convertedNodes);
        
        if (this.imageProcessingResult.processedImages.length > 0) {
          finalNodes = this.imageProcessor.appendImageInfoToNodes(
            convertedNodes,
            this.imageProcessingResult.processedImages
          );
          this.logger.info(`成功处理 ${this.imageProcessingResult.processedImages.length} 张图片`);
        }
        
        if (this.imageProcessingResult.failedImages.length > 0) {
          this.logger.warn(`图片处理失败: ${this.imageProcessingResult.failedImages.length} 个`, {
            failures: this.imageProcessingResult.failedImages.map(f => ({
              nodeId: f.nodeId,
              nodeName: f.nodeName,
              reason: f.reason
            }))
          });
        }
      } catch (error) {
        this.logger.error('图片处理失败', { error });
        // 图片处理失败不影响主流程
      }
    }
    
    this.processingStats.retained = finalNodes.length;
    this.processingStats.filtered = this.processingStats.totalOriginal - this.processingStats.retained;
    this.processingStats.filteringRate = this.processingStats.filtered / this.processingStats.totalOriginal;

    const result: PreprocessingResult = {
      validNodes: finalNodes,
      filteredElements: this.filteredElements,
      stats: this.processingStats
    };

    // 如果有图片处理结果，添加到结果中
    if (this.imageProcessingResult) {
      (result as any).imageProcessingResult = this.imageProcessingResult;
    }

    return result;
  }

  /**
   * 递归统计节点总数
   */
  private countTotalNodes(nodes: any[]): number {
    let count = 0;
    for (const node of nodes) {
      count++;
      if (node.children && Array.isArray(node.children)) {
        count += this.countTotalNodes(node.children);
      }
    }
    return count;
  }

  /**
   * 过滤有效节点
   */
  private filterValidNodes(nodes: any[]): any[] {
    const validNodes: any[] = [];

    for (const node of nodes) {
      // 检查透明元素
      if (this.isTransparentElement(node)) {
        this.addFilteredElement(node, 'transparent', 'opacity === 0');
        continue;
      }

      // 检查隐藏元素
      if (this.isHiddenElement(node)) {
        this.addFilteredElement(node, 'hidden', 'visible === false');
        continue;
      }

      // 检查空元素
      if (this.isEmptyElement(node)) {
        this.addFilteredElement(node, 'empty', 'no children, fills, or strokes');
        continue;
      }

      // 检查异常尺寸元素
      if (this.hasAbnormalDimensions(node)) {
        this.addFilteredElement(node, 'abnormal_size', 'extreme width/height ratio');
        continue;
      }

      // 检查无效类型
      if (this.isInvalidType(node)) {
        this.addFilteredElement(node, 'invalid_type', 'unsupported node type');
        continue;
      }

      // 递归处理子节点
      if (node.children && Array.isArray(node.children)) {
        node.children = this.filterValidNodes(node.children);
      }

      validNodes.push(node);
    }

    return validNodes;
  }

  /**
   * 转换节点类型
   */
  private convertNodeTypes(nodes: any[]): any[] {
    return nodes.map(node => {
      const converted = { ...node };
      
      // 标准化节点类型
      if (converted.type) {
        converted.type = this.normalizeNodeType(converted.type);
      }

      // 递归转换子节点
      if (converted.children && Array.isArray(converted.children)) {
        converted.children = this.convertNodeTypes(converted.children);
      }

      return converted;
    });
  }

  /**
   * 检查是否为透明元素
   */
  private isTransparentElement(node: any): boolean {
    return node.opacity !== undefined && node.opacity <= 0;
  }

  /**
   * 检查是否为隐藏元素
   */
  private isHiddenElement(node: any): boolean {
    return node.visible === false;
  }

  /**
   * 检查是否为空元素
   */
  private isEmptyElement(node: any): boolean {
    // 检查是否有子元素
    const hasChildren = node.children && Array.isArray(node.children) && node.children.length > 0;
    
    // 检查是否有填充
    const hasFills = node.fills && Array.isArray(node.fills) && node.fills.length > 0;
    
    // 检查是否有描边
    const hasStrokes = node.strokes && Array.isArray(node.strokes) && node.strokes.length > 0;
    
    // 检查是否有文本内容
    const hasText = node.characters && node.characters.trim().length > 0;

    // 如果没有任何内容，则认为是空元素
    return !hasChildren && !hasFills && !hasStrokes && !hasText;
  }

  /**
   * 检查是否有异常尺寸
   */
  private hasAbnormalDimensions(node: any): boolean {
    if (!node.absoluteBoundingBox) return false;
    
    const { width, height } = node.absoluteBoundingBox;
    
    // 检查极小尺寸
    if (width < 1 || height < 1) return true;
    
    // 检查极端宽高比
    const aspectRatio = Math.max(width, height) / Math.min(width, height);
    if (aspectRatio > 100) return true;
    
    return false;
  }

  /**
   * 检查是否为无效类型
   */
  private isInvalidType(node: any): boolean {
    const invalidTypes = ['CONNECTOR', 'STICKY', 'SHAPE_WITH_TEXT'];
    return invalidTypes.includes(node.type);
  }

  /**
   * 标准化节点类型
   */
  private normalizeNodeType(type: string): string {
    const typeMap: Record<string, string> = {
      'RECTANGLE': 'FRAME',
      'ELLIPSE': 'FRAME',
      'POLYGON': 'FRAME',
      'STAR': 'FRAME',
      'VECTOR': 'FRAME'
    };
    
    return typeMap[type] || type;
  }

  /**
   * 添加被过滤的元素
   */
  private addFilteredElement(node: any, reason: FilterReason, details: string): void {
    this.filteredElements.push({
      id: node.id || 'unknown',
      name: node.name || 'unnamed',
      type: node.type || 'unknown',
      reason,
      details
    });
  }
}

/**
 * 数据预处理验证规则
 */
export const dataPreprocessingRule: Rule = {
  name: "data-preprocessing",
  description: "预处理 Figma 数据，过滤无效元素并优化数据结构",
  category: "structure",
  severity: "warning",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始数据预处理验证`);

    try {
      const preprocessor = new FigmaDataPreprocessor({
        fileKey: context.figmaFile.metadata?.fileKey,
        accessToken: context.figmaFile.metadata?.accessToken
      });
      const nodes = context.node.children || [context.node];
      
      const result = await preprocessor.preprocessNodes(nodes);
      
      const issues: string[] = [];
      const suggestions: string[] = [];

      // 分析过滤统计
      if (result.stats.filteringRate > 0.5) {
        issues.push(`过滤率过高: ${(result.stats.filteringRate * 100).toFixed(1)}%`);
        suggestions.push("检查设计文件是否包含过多隐藏或无效元素");
      }

      // 分析被过滤的元素类型
      const filterReasons = result.filteredElements.reduce((acc: Record<string, number>, elem: FilteredElement) => {
        acc[elem.reason] = (acc[elem.reason] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      if (filterReasons.transparent > 10) {
        suggestions.push("考虑清理透明度为0的元素");
      }

      if (filterReasons.hidden > 5) {
        suggestions.push("考虑删除隐藏的元素");
      }

      if (filterReasons.empty > 20) {
        suggestions.push("考虑清理空的容器元素");
      }

      const valid = issues.length === 0;
      
      logger.info(`[${traceId}] 数据预处理完成: 原始${result.stats.totalOriginal}个, 保留${result.stats.retained}个, 过滤${result.stats.filtered}个`);

      return {
        valid,
        message: valid
          ? `数据预处理完成，保留 ${result.stats.retained}/${result.stats.totalOriginal} 个元素`
          : `数据预处理发现问题: ${issues.join(", ")}`,
        severity: "warning",
        suggestions,
        fixes: result.filteredElements.length > 0 ? [{
          type: 'remove',
          target: 'filtered-elements',
          value: result.filteredElements,
          description: `移除 ${result.filteredElements.length} 个无效元素`
        }] : undefined
      };
    } catch (error) {
      logger.error(`[${traceId}] 数据预处理失败: ${error}`);
      return {
        valid: false,
        message: `数据预处理失败: ${error}`,
        severity: "error",
        suggestions: ["检查数据格式是否正确"]
      };
    }
  }
};

/**
 * 数据压缩规则
 */
export const dataCompressionRule: Rule = {
  name: "data-compression",
  description: "压缩和优化 Figma 数据以提高处理效率",
  category: "structure",
  severity: "info",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始数据压缩分析`);

    try {
      const compressionStats = analyzeCompressionOpportunities(context.node);
      
      const suggestions: string[] = [];
      
      if (compressionStats.duplicateStyles > 10) {
        suggestions.push(`发现 ${compressionStats.duplicateStyles} 个重复样式，可以合并优化`);
      }

      if (compressionStats.unusedComponents > 5) {
        suggestions.push(`发现 ${compressionStats.unusedComponents} 个未使用的组件，可以清理`);
      }

      if (compressionStats.deepNesting > 10) {
        suggestions.push(`发现 ${compressionStats.deepNesting} 层深度嵌套，建议简化层级结构`);
      }

      const compressionRatio = compressionStats.potentialSavings / compressionStats.originalSize;
      
      logger.info(`[${traceId}] 数据压缩分析完成: 原始大小${compressionStats.originalSize}, 可优化${compressionStats.potentialSavings}`);

      return {
        valid: true,
        message: `数据压缩分析完成，可优化 ${(compressionRatio * 100).toFixed(1)}% 的数据`,
        severity: "info",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 数据压缩分析失败: ${error}`);
      return {
        valid: false,
        message: `数据压缩分析失败: ${error}`,
        severity: "error",
        suggestions: ["检查数据结构是否正确"]
      };
    }
  }
};

/**
 * Figma数据压缩器
 * 负责压缩变量名、去重样式、优化数据结构
 */
export class FigmaDataCompressor {
  private variableMap: Map<string, string> = new Map();
  private variableCounters: Record<string, number> = {
    layout: 0,
    fill: 0,
    stroke: 0,
    effect: 0,
    style: 0,
    textStyle: 0
  };

  /**
   * 压缩Figma数据
   */
  compressData(data: any): { compressed: any; compressionStats: any } {
    const originalSize = JSON.stringify(data).length;
    
    // 深拷贝数据以避免修改原始数据
    const compressed = JSON.parse(JSON.stringify(data));
    
    // 压缩全局变量
    if (compressed.globalVars && compressed.globalVars.styles) {
      compressed.globalVars.styles = this.compressGlobalStyles(compressed.globalVars.styles);
    }
    
    // 压缩节点数据
    if (compressed.nodes) {
      compressed.nodes = this.compressNodes(compressed.nodes);
    }
    
    const compressedSize = JSON.stringify(compressed).length;
    const compressionRatio = (originalSize - compressedSize) / originalSize;
    
    const compressionStats = {
      originalSize,
      compressedSize,
      savedBytes: originalSize - compressedSize,
      compressionRatio,
      compressionPercentage: compressionRatio * 100,
      variablesCompressed: this.variableMap.size
    };
    
    return { compressed, compressionStats };
  }

  /**
   * 压缩全局样式变量
   */
  private compressGlobalStyles(styles: Record<string, any>): Record<string, any> {
    const compressedStyles: Record<string, any> = {};
    
    for (const [originalKey, value] of Object.entries(styles)) {
      const compressedKey = this.getCompressedVariableName(originalKey);
      compressedStyles[compressedKey] = value;
    }
    
    return compressedStyles;
  }

  /**
   * 压缩节点数据
   */
  private compressNodes(nodes: any[]): any[] {
    return nodes.map(node => this.compressNode(node));
  }

  /**
   * 压缩单个节点
   */
  private compressNode(node: any): any {
    const compressed = { ...node };
    
    // 压缩样式引用
    if (compressed.layout && typeof compressed.layout === 'string') {
      compressed.layout = this.getCompressedVariableName(compressed.layout);
    }
    
    if (compressed.fills && typeof compressed.fills === 'string') {
      compressed.fills = this.getCompressedVariableName(compressed.fills);
    }
    
    if (compressed.strokes && typeof compressed.strokes === 'string') {
      compressed.strokes = this.getCompressedVariableName(compressed.strokes);
    }
    
    if (compressed.effects && typeof compressed.effects === 'string') {
      compressed.effects = this.getCompressedVariableName(compressed.effects);
    }
    
    if (compressed.textStyle && typeof compressed.textStyle === 'string') {
      compressed.textStyle = this.getCompressedVariableName(compressed.textStyle);
    }
    
    // 递归处理子节点
    if (compressed.children && Array.isArray(compressed.children)) {
      compressed.children = this.compressNodes(compressed.children);
    }
    
    return compressed;
  }

  /**
   * 获取压缩后的变量名
   */
  private getCompressedVariableName(originalName: string): string {
    if (this.variableMap.has(originalName)) {
      return this.variableMap.get(originalName)!;
    }
    
    // 确定变量类型前缀
    let prefix = 'v';
    if (originalName.startsWith('layout_')) prefix = 'l';
    else if (originalName.startsWith('fill_')) prefix = 'f';
    else if (originalName.startsWith('stroke_')) prefix = 's';
    else if (originalName.startsWith('effect_')) prefix = 'e';
    else if (originalName.startsWith('style_') || originalName.startsWith('textStyle_')) prefix = 't';
    
    // 生成压缩后的变量名
    const counterKey = this.getCounterKey(prefix);
    this.variableCounters[counterKey]++;
    const compressedName = `${prefix}${this.variableCounters[counterKey]}`;
    
    this.variableMap.set(originalName, compressedName);
    return compressedName;
  }

  /**
   * 获取计数器键名
   */
  private getCounterKey(prefix: string): string {
    switch (prefix) {
      case 'l': return 'layout';
      case 'f': return 'fill';
      case 's': return 'stroke';
      case 'e': return 'effect';
      case 't': return 'style';
      default: return 'style';
    }
  }

  /**
   * 重置压缩器状态
   */
  reset(): void {
    this.variableMap.clear();
    this.variableCounters = {
      layout: 0,
      fill: 0,
      stroke: 0,
      effect: 0,
      style: 0,
      textStyle: 0
    };
  }
}

/**
 * 分析压缩机会
 */
function analyzeCompressionOpportunities(node: any): {
  originalSize: number;
  potentialSavings: number;
  duplicateStyles: number;
  unusedComponents: number;
  deepNesting: number;
} {
  const stats = {
    originalSize: 0,
    potentialSavings: 0,
    duplicateStyles: 0,
    unusedComponents: 0,
    deepNesting: 0
  };

  // 计算原始大小（估算）
  stats.originalSize = JSON.stringify(node).length;

  // 分析重复样式
  const styles = extractStyles(node);
  const uniqueStyles = new Set(styles.map(s => JSON.stringify(s)));
  stats.duplicateStyles = styles.length - uniqueStyles.size;

  // 分析嵌套深度
  stats.deepNesting = calculateMaxDepth(node);

  // 估算潜在节省
  stats.potentialSavings = stats.duplicateStyles * 100 + stats.deepNesting * 50;

  return stats;
}

/**
 * 提取样式信息
 */
function extractStyles(node: any): any[] {
  const styles: any[] = [];
  
  if (node.fills) styles.push({ type: 'fill', value: node.fills });
  if (node.strokes) styles.push({ type: 'stroke', value: node.strokes });
  if (node.effects) styles.push({ type: 'effect', value: node.effects });
  if (node.style) styles.push({ type: 'text', value: node.style });

  // 递归处理子节点
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      styles.push(...extractStyles(child));
    }
  }

  return styles;
}

/**
 * 计算最大嵌套深度
 */
function calculateMaxDepth(node: any, currentDepth = 0): number {
  if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
    return currentDepth;
  }

  let maxChildDepth = currentDepth;
  for (const child of node.children) {
    const childDepth = calculateMaxDepth(child, currentDepth + 1);
    maxChildDepth = Math.max(maxChildDepth, childDepth);
  }

  return maxChildDepth;
}